import { useNavigation } from '@/hooks/useNavigation';
import { Icon } from '@iconify/react';
import {
  Badge,
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
} from '@nextui-org/react';
import React from 'react';
import toast from 'react-hot-toast';
import DeleteConfirmationModal from '../../../components/modal/DeleteConfirmationModal';
import useDeletePrompt from '../../../hooks/queries/prompts/useDeletePrompt';
import TagsModal from './tags-modal';
import VoiceSettingsModal from './voice-settings-modal';

interface HeaderProps {
  agent: {
    id?: string | number | null;
    name: string;
    version: string;
    prompt?: string;
    enable_audio?: boolean;
    tags?: any[];
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setAgent: (agent: any) => void;
  isCreateLoading: boolean;
  handleSave: () => void;
  isRunLoading: boolean;
  handleRun: () => void;
  setIsSettingsOpen: (isOpen: boolean) => void;
  posts: Array<{ text: string }>;
  onCreatePrompt: () => void;
  refetchPrompt: () => void;
  nameError?: string;
  onNameChange?: (name: string) => void;
}

export default function Header({
  agent,
  setAgent,
  isCreateLoading,
  handleSave,
  isRunLoading,
  handleRun,
  setIsSettingsOpen,
  onCreatePrompt,
  refetchPrompt,
  nameError,
  onNameChange,
}: HeaderProps) {
  const { navigateToTenantRoute } = useNavigation();
  const [tagsModalOpen, setTagsModalOpen] = React.useState(false);
  const [voiceSettingsModalOpen, setVoiceSettingsModalOpen] =
    React.useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const deletePrompt = useDeletePrompt();

  console.log(agent);

  const handleCreate = () => {
    onCreatePrompt();
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
    setIsPopoverOpen(false);
  };

  const confirmDelete = () => {
    if (agent.id) {
      deletePrompt.mutate(Number(agent.id), {
        onSuccess: () => {
          toast.success('Agent has been deleted');
          navigateToTenantRoute('/agents');
        },
      });
    }
  };

  const handleVoiceSettingsSave = () => {
    handleSave();
    setVoiceSettingsModalOpen(false);
  };

  return (
    <div className="border-b border-divider py-4">
      <div className="flex justify-between relative flex-col items-start md:flex-row md:items-centerflex flex-col items-start justify-start md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-4">
          <Tooltip content="Create prompt">
            <Icon
              icon="material-symbols:add-box-outline"
              className="w-10 h-10 text-default-500 cursor-pointer"
              onClick={handleCreate}
            />
          </Tooltip>

          <Input
            label="Name"
            placeholder="Untitled"
            value={agent.name}
            size="sm"
            isRequired
            isInvalid={Boolean(nameError)}
            errorMessage={nameError}
            onChange={(e) =>
              onNameChange
                ? onNameChange(e.target.value)
                : setAgent({ ...agent, name: e.target.value })
            }
          />
          <Badge
            content={agent.version}
            size="lg"
            variant="solid"
            color="default"
          >
            {''}
          </Badge>
          {/* <Tooltip content="Tag Settings">
            <Button
              isIconOnly
              variant="light"
              onPress={() => setTagsModalOpen(true)}
            >
              <Icon icon="material-symbols:filter-list" className="w-5 h-5" />
            </Button>
          </Tooltip> */}
        </div>
        <div className="flex items-center gap-2 mt-2 md:mt-0">
          <Tooltip content="Model Settings">
            <Button
              isIconOnly
              variant="light"
              onPress={() => setIsSettingsOpen(true)}
            >
              <Icon icon="material-symbols:filter-list" className="w-5 h-5" />
            </Button>
          </Tooltip>

          <Button
            color="primary"
            isLoading={isCreateLoading}
            onPress={handleSave}
          >
            {agent?.id ? 'Update Prompt' : 'Save Prompt'}
          </Button>
          <Button
            color="primary"
            startContent={
              isRunLoading ? (
                <Icon
                  icon="material-symbols:progress-activity"
                  className="w-4 h-4 animate-spin"
                />
              ) : (
                <Icon icon="material-symbols:play-arrow" className="w-4 h-4" />
              )
            }
            isDisabled={!agent.prompt}
            onPress={handleRun}
          >
            Run
          </Button>
          <Popover
            placement="bottom-end"
            size="sm"
            isOpen={isPopoverOpen}
            onOpenChange={setIsPopoverOpen}
          >
            <PopoverTrigger>
              <Button isIconOnly variant="light">
                <Icon icon="material-symbols:more-vert" className="w-5 h-5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent>
              <div className="p-2 flex flex-col">
                <Button
                  variant="light"
                  className="justify-start"
                  onPress={() => {
                    setVoiceSettingsModalOpen(true);
                    setIsPopoverOpen(false);
                  }}
                  startContent={
                    <Icon icon="mdi:microphone" className="w-5 h-5" />
                  }
                >
                  Voice Settings
                </Button>

                <Button
                  variant="light"
                  className="justify-start "
                  onPress={() => {
                    setTagsModalOpen(true);
                    setIsPopoverOpen(false);
                  }}
                  startContent={
                    <Icon
                      icon="material-symbols:filter-list"
                      className="w-5 h-5"
                    />
                  }
                >
                  Tags
                </Button>

                <Button
                  variant="light"
                  className="justify-start text-danger"
                  startContent={
                    <Icon
                      icon="material-symbols:delete-outline"
                      className="w-4 h-4"
                    />
                  }
                  onPress={handleDelete}
                  isDisabled={!agent.id}
                  isLoading={deletePrompt.isPending}
                >
                  Delete Agent
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={confirmDelete}
          title="Delete Agent"
          entityName="agent"
          isLoading={deletePrompt.isPending}
        />
      </div>

      <TagsModal
        isOpen={tagsModalOpen}
        tags={agent.tags || []}
        onClose={() => setTagsModalOpen(false)}
        refetchPrompt={refetchPrompt}
        agent={{
          id: agent.id?.toString() || '',
          name: agent.name,
          version: agent.version,
          prompt: agent.prompt || '',
        }}
      />

      <VoiceSettingsModal
        isOpen={voiceSettingsModalOpen}
        onClose={() => setVoiceSettingsModalOpen(false)}
        enableAudio={agent.enable_audio || false}
        onEnableAudioChange={(enabled) =>
          setAgent({ ...agent, enable_audio: enabled })
        }
        onSave={handleVoiceSettingsSave}
        isLoading={isCreateLoading}
      />
    </div>
  );
}
